import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { ScanEye, Loader2, RefreshCw, Wrench, ClipboardList, AlertTriangle } from "lucide-react";

/**
 * Panel de calidad OCR: qué % de partes de trabajo manuscritos y de partes de
 * taller necesitaron el fallback a Claude Vision (letra difícil de leer con
 * el modo nativo PDF). Ayuda a detectar centros con letra poco legible.
 */
export default function OcrStatsTab() {
  const { authAxios } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await authAxios.get("/admin/ocr-fallback-stats", { params });
      setStats(data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [dateFrom, dateTo]);

  const pctColor = (pct) => pct >= 40 ? "text-red-600" : pct >= 15 ? "text-amber-600" : "text-emerald-600";
  const barColor = (pct) => pct >= 40 ? "#DC2626" : pct >= 15 ? "#D97706" : "#059669";

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="ocr-stats-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-bold text-slate-800 text-base flex items-center gap-2">
            <ScanEye size={18} className="text-[#007934]" /> Calidad OCR — Fallback a Visión
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            % de documentos manuscritos que necesitaron reintento con Claude Vision (letra difícil de leer con el OCR nativo).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" data-testid="ocr-stats-date-from" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs bg-white" title="Desde" />
          <span className="text-slate-400 text-xs">→</span>
          <input type="date" data-testid="ocr-stats-date-to" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs bg-white" title="Hasta" />
          <button data-testid="ocr-stats-refresh" onClick={refresh} disabled={loading}
            className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {loading && !stats ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 size={22} className="animate-spin" />
        </div>
      ) : !stats ? null : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="card-white p-5">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardList size={16} className="text-slate-500" />
                <h3 className="text-sm font-bold text-slate-700">Partes de Trabajo (manuscritos)</h3>
              </div>
              <div className="flex items-baseline gap-2">
                <span className={`text-3xl font-black ${pctColor(stats.work_reports.pct)}`} data-testid="ocr-stats-wr-pct">
                  {stats.work_reports.pct}%
                </span>
                <span className="text-xs text-slate-400">necesitaron modo Visión</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                {stats.work_reports.vision_fallback} de {stats.work_reports.total} documentos procesados
              </p>
              <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.min(stats.work_reports.pct, 100)}%`, backgroundColor: barColor(stats.work_reports.pct) }} />
              </div>
            </div>

            <div className="card-white p-5">
              <div className="flex items-center gap-2 mb-3">
                <Wrench size={16} className="text-[#EA580C]" />
                <h3 className="text-sm font-bold text-slate-700">Taller (Parque Central de Maquinaria)</h3>
              </div>
              <div className="flex items-baseline gap-2">
                <span className={`text-3xl font-black ${pctColor(stats.workshop.pct)}`} data-testid="ocr-stats-ws-pct">
                  {stats.workshop.pct}%
                </span>
                <span className="text-xs text-slate-400">necesitaron modo Visión</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                {stats.workshop.vision_fallback} de {stats.workshop.total} actuaciones procesadas
              </p>
              <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.min(stats.workshop.pct, 100)}%`, backgroundColor: barColor(stats.workshop.pct) }} />
              </div>
            </div>
          </div>

          <div className="card-white overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
                <AlertTriangle size={15} className="text-slate-500" /> Desglose por contrato — Partes de Trabajo
              </h3>
            </div>
            {(!stats.work_reports.by_contract || stats.work_reports.by_contract.length === 0) ? (
              <div className="text-center py-10 text-slate-400 text-sm">Sin documentos procesados en el rango seleccionado</div>
            ) : (
              <table className="w-full data-table" data-testid="ocr-stats-by-contract-table">
                <thead>
                  <tr>
                    <th>Contrato</th>
                    <th>Documentos</th>
                    <th>Fallback a Visión</th>
                    <th>% Fallback</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.work_reports.by_contract.map(c => (
                    <tr key={c.contract_id}>
                      <td className="font-medium text-slate-700">{c.contract_name}</td>
                      <td>{c.total}</td>
                      <td>{c.vision_fallback}</td>
                      <td className={`font-bold ${pctColor(c.pct)}`}>{c.pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
